import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { FileText, LogIn as LogInIcon, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";

  if (user) return <Navigate to={from} replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Email and password are required");
      return;
    }
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6">
      <Card className="w-full max-w-md p-8" data-testid="login-card">
        <div className="mb-6 pb-5 border-b border-zinc-100">
          <p
            className="text-sm font-medium text-zinc-700 leading-snug"
            data-testid="login-tagline"
          >
            Tired of making invoices one by one? BulkInvoice does it in seconds.
          </p>
          <p
            className="text-xs text-zinc-500 mt-2"
            data-testid="login-support-email"
          >
            Need help? Email us at{" "}
            <a
              href="mailto:support@bulkinvoice.in"
              className="text-blue-600 hover:underline font-medium"
            >
              support@bulkinvoice.in
            </a>
          </p>
        </div>

        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-md bg-blue-600 text-white flex items-center justify-center">
            <FileText size={18} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-zinc-500">Welcome</div>
            <h1 className="text-xl font-bold tracking-tight">Sign in to PO Invoice</h1>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Email</Label>
            <Input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-10 mt-1 text-sm"
              placeholder="you@company.in"
              data-testid="login-email"
              required
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Password</Label>
              <Link
                to="/forgot-password"
                className="text-[11px] text-blue-600 hover:underline"
                data-testid="link-forgot-password"
              >
                Forgot password?
              </Link>
            </div>
            <Input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-10 mt-1 text-sm"
              placeholder="••••••••"
              data-testid="login-password"
              required
            />
          </div>
          <Button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700 h-10"
            disabled={loading}
            data-testid="login-submit"
          >
            {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : <LogInIcon size={16} className="mr-2" />}
            {loading ? "Signing in…" : "Sign In"}
          </Button>
        </form>

        <div className="text-xs text-zinc-500 text-center mt-6">
          New here?{" "}
          <Link to="/signup" className="text-blue-600 hover:underline font-medium" data-testid="link-signup">
            Create an account
          </Link>
        </div>
      </Card>
    </div>
  );
}
